import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../_base/crud';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth/_selectors/auth.selectors';
import { environment } from '../../../../environments/environment';
import { Agreement } from '../_models/agreement.model';
import { AgreementTemplate } from '../_models/agreement-template.model';
import { filter, map } from 'rxjs/operators';
import { AgreementType } from '../_models/agreement-type.model';
// Real REST API
@Injectable({
  providedIn: 'root',
})
export class AgreementService {
  private authToken;
  private companyId;
  private subscriptions: Subscription[];

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));

    this.store
      .pipe(
        select(currentUser),
        filter((res) => !!res.id),
      )
      .subscribe((res) => {
        this.companyId = res.companyID;
      });
  }

  findByEntityContact(contactId: number, entityId: number): Observable<any> {
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.AGREEMENT_BASE +
      '/companies/' +
      this.companyId +
      '/agreements/?entity_id=' +
      entityId +
      '&contact_id=' +
      contactId;

    return this.http.get<any>(url, { headers: headers });
  }

  revokeAgreement(agreementId: number, reason: string): Observable<any> {
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.AGREEMENT_BASE +
      '/companies/' +
      this.companyId +
      '/agreements/' +
      agreementId +
      '/revoke';

    return this.http.post<any>(url, { reason }, { headers: headers });
  }

  remindAgreement(agreementId: number): Observable<any> {
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.AGREEMENT_BASE +
      '/companies/' +
      this.companyId +
      '/agreements/' +
      agreementId +
      '/remind';

    return this.http.put<any>(url, { headers: headers });
  }

  // CREATE =>  POST: add a new agreement to the server
  createAgreement(data: any): Observable<Agreement> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.AGREEMENT_BASE + '/companies/' + this.companyId + '/agreements/';

    return this.http.post<Agreement>(url, data, { headers: httpHeaders });
  }

  // UPDATE => PUT: update the agreement on the server
  updateAgreement(agreement: Agreement): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.put(environment.AGREEMENT_BASE + '/agreement', agreement, {
      headers: httpHeaders,
    });
  }

  // DELETE => delete the agreement from the server
  deleteAgreement(agreementId: number): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      id: agreementId,
    };
    return this.http.put<Agreement>(environment.AGREEMENT_BASE + '/agreement', body, {
      headers: httpHeaders,
    });
  }

  // Agreements
  getAllAgreements(companyID: number): Observable<Agreement[]> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.AGREEMENT_BASE + '/companies/' + companyID + '/agreements';
    return this.http.get<Agreement[]>(url, { headers: httpHeaders });
  }

  // DELETE => delete the agreements from the server
  deleteAgreements(agreementIds: number[]): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      ids: agreementIds,
    };
    return this.http.put<Agreement>(environment.AGREEMENT_BASE + '/agreements', body, {
      headers: httpHeaders,
    });
  }

  findAgreements(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({
      fromString: AgreementService.getAgreementsListQueryString(queryParams),
    });
    return this.http.get<any>(
      environment.AGREEMENT_BASE + '/companies/' + this.companyId + '/agreements',
      { params },
    );
  }

  private static getAgreementsListQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}&is_active=${filter.isActive}`;
    } else {
      if (filter.id) fromString += `&id_has=${filter.id}`;
      if (filter.name) fromString += `&name_has=${filter.name}`;
      if (filter.type) fromString += `&type_has=${filter.type}`;
      if (filter.status) fromString += `&status_has=${filter.status}`;
      if (filter.reason) fromString += `&revoke_reason_has=${filter.reason}`;
      if (filter.voidDate) fromString += `&void_date_has=${filter.voidDate}`;
      if (filter.dateSent) fromString += `&date_sent_has=${filter.dateSent}`;
    }

    return fromString;
  }
  private static getAgreementTemplatesListQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}&is_active=${filter.isActive}`;
    } else {
      if (filter.id) fromString += `&id_has=${filter.id}`;
      if (filter.name) fromString += `&name_has=${filter.name}`;
      if (filter.type) fromString += `&type_has=${filter.type}`;
      if (filter.dateCreated) fromString += `&created_has=${filter.dateCreated}`;
    }

    return fromString;
  }

  searchAgreements(term: string): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.AGREEMENT_BASE + '/agreements/autocomplete?term=' + term;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  // TODO: Add actual implementation

  createTemplate(agreementTemplate: AgreementTemplate, officeId: number): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);

    const url = environment.AGREEMENT_BASE + '/companies/' + this.companyId + '/templates';

    const data = { ...agreementTemplate, office_id: officeId };

    return this.http.post<{ data: any }>(url, data, { headers: httpHeaders });
  }

  updateTemplate(agreementTemplate: AgreementTemplate, officeId: number) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);

    const url = environment.AGREEMENT_BASE + '/companies/' + this.companyId + '/templates';

    const data = { ...agreementTemplate, office_id: officeId };

    return this.http.put<{ data: any }>(url, data, { headers: httpHeaders });
  }

  deleteAgreementTemplate(agreementTemplateId: number, office_id: number) {
    const url =
      environment.AGREEMENT_BASE +
      '/companies/' +
      this.companyId +
      '/templates/' +
      agreementTemplateId +
      '/delete';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);

    return this.http.post<any>(url, { office_id }, { headers: httpHeaders });
  }

  activateAgreementTemplate(agreementTemplateId: number) {
    const url =
      environment.AGREEMENT_BASE +
      '/companies/' +
      this.companyId +
      '/templates/' +
      agreementTemplateId +
      '/activate';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);

    return this.http.put<any>(url, { headers: httpHeaders });
  }

  findAgreementTemplates(queryParams: QueryParamsModel) {
    const params = new HttpParams({
      fromString: AgreementService.getAgreementTemplatesListQueryString(queryParams),
    });
    return this.http.get<any>(
      environment.AGREEMENT_BASE + '/companies/' + this.companyId + '/templates',
      { params },
    );
  }

  getAllAgreementTemplates(companyId: number): Observable<AgreementTemplate[]> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.AGREEMENT_BASE + '/companies/' + companyId + '/templates?page=0&size=9999';

    return this.http.get<any>(url, { headers: httpHeaders }).pipe(map((res) => res.data));
  }

  getTypes(): Observable<AgreementType[]> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.AGREEMENT_BASE + '/companies/' + this.companyId + '/agreements/types';
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  getAgreement(id: any): Observable<Agreement> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.AGREEMENT_BASE + '/companies/' + this.companyId + '/agreements/' + id;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  getTemplate(id: number) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.AGREEMENT_BASE + '/companies/' + this.companyId + '/templates/' + id;
    return this.http.get<any>(url, { headers: httpHeaders });
  }
}
